import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, CheckSquare, MessageSquare, Wallet, ChevronRight, TrendingUp, Bell } from "lucide-react";
import { money, monthKey, prettyMonth, timeAgo } from "@/mobile/mobileUtils";

export default function MobileHome() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [expense, setExpense] = useState(null);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get("/dashboard/admin?range=month").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/notifications").then((r) => setNotes((r.data || []).slice(0, 4))).catch(() => {});
    if (isAdmin) api.get(`/expenses-dashboard?month=${monthKey()}`).then((r) => setExpense(r.data)).catch(() => {});
  }, [isAdmin]);

  const kpis = [
    { label: "Active Cases", value: stats?.active_cases, to: "/m/clients", icon: Users },
    { label: "Tasks Due Today", value: stats?.tasks_due_today, to: "/m/tasks", icon: CheckSquare },
    { label: "New Leads", value: stats?.new_leads, to: "/m/clients", icon: TrendingUp },
    { label: "Follow-ups Due", value: stats?.followups_due, to: "/m/clients", icon: Bell },
  ];

  return (
    <div className="p-4 space-y-5" data-testid="mobile-home">
      <div className="animate-fadeup">
        <h1 className="font-head font-extrabold text-2xl text-slate-900">Hi, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-slate-500 text-sm">Here's your day at Chahal Overseas.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => (
          <Link key={k.label} to={k.to} data-testid={`home-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm active:scale-[0.98] transition-transform">
            <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2"><k.icon className="h-5 w-5" /></div>
            <div className="font-head font-extrabold text-2xl text-slate-900">{k.value ?? "—"}</div>
            <div className="text-xs text-slate-500">{k.label}</div>
          </Link>
        ))}
      </div>

      {isAdmin && (
        <Link to="/m/expenses" data-testid="home-expense-card" className="block bg-gradient-to-br from-secondary to-[#123a63] text-white rounded-2xl p-4 shadow-sm active:scale-[0.99] transition-transform">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-200"><Wallet className="h-4 w-4" /> Expenses · {prettyMonth(monthKey())}</div>
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </div>
          <div className="font-head font-extrabold text-3xl mt-2">{expense ? money(expense.total, expense.currency) : "—"}</div>
          <div className="text-xs text-slate-300">{expense ? `${expense.count} expense${expense.count === 1 ? "" : "s"} this month` : "\u00A0"}</div>
        </Link>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-head font-bold text-slate-900">Quick actions</h2>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Link to="/m/chat" data-testid="home-action-chat" className="bg-white rounded-2xl p-3 border border-slate-100 flex flex-col items-center gap-1.5 text-xs font-medium text-slate-600 active:scale-95 transition-transform">
            <MessageSquare className="h-5 w-5 text-primary" /> Team Chat
          </Link>
          <Link to="/m/clients" data-testid="home-action-clients" className="bg-white rounded-2xl p-3 border border-slate-100 flex flex-col items-center gap-1.5 text-xs font-medium text-slate-600 active:scale-95 transition-transform">
            <Users className="h-5 w-5 text-primary" /> Find Client
          </Link>
          <Link to="/m/tasks" data-testid="home-action-tasks" className="bg-white rounded-2xl p-3 border border-slate-100 flex flex-col items-center gap-1.5 text-xs font-medium text-slate-600 active:scale-95 transition-transform">
            <CheckSquare className="h-5 w-5 text-primary" /> My Tasks
          </Link>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-head font-bold text-slate-900">Recent updates</h2>
          <Link to="/m/notifications" className="text-xs text-primary font-semibold">See all</Link>
        </div>
        <div className="space-y-2">
          {notes.length === 0 && <div className="text-sm text-slate-400 bg-white rounded-2xl p-4 border border-slate-100">No recent notifications.</div>}
          {notes.map((n) => (
            <div key={n.id} className="bg-white rounded-2xl p-3.5 border border-slate-100 flex items-start gap-3" data-testid="home-notification">
              <div className={`h-2 w-2 rounded-full mt-2 shrink-0 ${n.read ? "bg-slate-200" : "bg-[hsl(var(--brand-orange))]"}`} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{n.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
